import React, { useState, useEffect, useCallback, useId, memo } from 'react';
import { FiX, FiFileText, FiCalendar, FiDownload, FiMail, FiLoader, FiDollarSign, FiMapPin, FiHelpCircle } from 'react-icons/fi';
import { jobApi, resumeApi, applicationApi } from '../../services/api';
import { toast } from 'react-toastify';
import ResumeStyleModal from '../resumes/ResumeStyleModal';

const ApplicationDetailModal = memo(({ isOpen, onClose, application, initialTab = 'resume' }) => {
  const titleId = useId();
  const [resumeData, setResumeData] = useState(null);
  const [coverLetterData, setCoverLetterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab); // 'resume' or 'coverLetter'
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  useEffect(() => {
    if (isOpen && application) {
      fetchApplicationDetails();
    }
  }, [isOpen, application]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  
  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
       
      // Fetch specific version directly from the versions API instead of searching in the versions array
      let versionData = null;
      if (!application.resumeId) {
        console.warn('Missing resumeId for application');
      } else {
        try {
          if (!application.versionId || application.versionId === 'latest') {
            const resumeResponse = await resumeApi.getResumeById(application.resumeId);
            const versions = resumeResponse?.data?.versions || [];
            versionData = versions.length > 0 ? versions[versions.length - 1] : null;
          } else {
            const versionResponse = await resumeApi.getResumeVersion(
              application.resumeId, 
              application.versionId
            );
            versionData = versionResponse.data;
          }
        } catch (err) {
          console.error('Error fetching resume version:', err);
          versionData = null;
        }
      }
      
      if (versionData) {
        setResumeData({
          ...versionData,
          content: versionData?.content || {},
          versionType: versionData?.versionType || 'Unknown',
          versionDate: versionData?.createdAt || new Date()
        });
      } else {
        setResumeData(null);
      }
      
      // Fetch cover letter if available
      if (application.coverLetterId) {
        try {
          const clResponse = await applicationApi.getCoverLetterById(application.coverLetterId);
          setCoverLetterData(clResponse.data);
          // If we have a cover letter, set the active tab to it
          setActiveTab('coverLetter');
        } catch (err) {
          console.error('Error fetching cover letter:', err);
          // Don't throw error if cover letter not found - it might not exist
          setCoverLetterData(null);
        }
      } else {
        setCoverLetterData(null);
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
    } finally {
      setLoading(false);
    }
  };
  const generatePDF = useCallback(async (type) => {
    try {
      setGeneratingPDF(true);
      if (type === 'resume') {
        setShowStyleModal(true);
      } else if (type === 'coverLetter' && application.coverLetterId) {
        await applicationApi.generateCoverLetterPDF(application.coverLetterId);
        toast.success('Cover letter PDF downloaded successfully');
      }
    } catch (error) {
      console.error(`Error generating ${type} PDF:`, error);
      toast.error(`Failed to download ${type} PDF`);
    } finally {
      setGeneratingPDF(false);
    }
  }, [application]);
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const toDisplayText = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.details != null) return typeof value.details === 'string' ? value.details : (Array.isArray(value.details) ? value.details.map(toDisplayText).join(' ') : String(value.details));
    if (typeof value === 'object' && value.text != null) return String(value.text);
    return String(value);
  };

  const renderExperienceDetails = (exp) => {
    const bullets = exp?.details ?? exp?.bullets ?? exp?.highlights ?? [];
    const list = Array.isArray(bullets) ? bullets : [];
    if (list.length > 0) {
      return list.map((detail, idx) => (
        <li key={idx}>{toDisplayText(detail)}</li>
      ));
    }
    if (exp?.has_projects && Array.isArray(exp?.projects) && exp.projects.length > 0) {
      return exp.projects.map((project, idx) => (
        <li key={idx}>
          <div className="font-medium text-sm">{project.name}</div>
          {Array.isArray(project.details) && project.details.length > 0 && (
            <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
              {project.details.map((detail, detailIdx) => (
                <li key={detailIdx}>{toDisplayText(detail)}</li>
              ))}
            </ul>
          )}
        </li>
      ));
    }
    return <li className="text-gray-500">No details available</li>;
  };
  
  if (!isOpen || !application) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-start">
              <h3 id={titleId} className="text-lg leading-6 font-medium text-gray-900">
                {application.company} - {application.jobTitle}
              </h3>
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
                aria-label="Close modal"
              >
                <span className="sr-only">Close</span>
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            {/* Application metadata */}
            <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 gap-4">
              <span className="flex items-center">
                <FiCalendar className="mr-1 h-4 w-4 text-gray-400" />
                Applied on {formatDate(application.appliedAt)}
              </span>
              <span className="flex items-center">
                <FiFileText className="mr-1 h-4 w-4 text-gray-400" />
                Status: {application.status ? application.status.charAt(0).toUpperCase() + application.status.slice(1) : 'Unknown'}
              </span>
              {application.coverLetterId && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center">
                  <FiMail className="mr-1 h-3 w-3" />
                  With Cover Letter
                </span>
              )}
            </div>

            {/* Add location and salary if available */}
            <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 gap-4">
              {application.location && (
                <span className="flex items-center">
                  <FiMapPin className="mr-1 h-4 w-4 text-gray-400" />
                  Location: {application.location}
                </span>
              )}
              {application.salary_range && (
                <span className="flex items-center">
                  <FiDollarSign className="mr-1 h-4 w-4 text-gray-400" />
                  Salary: {application.salary_range}
                </span>
              )}
            </div>
            
            {/* Tab navigation */}
            <div className="border-b border-gray-200 mt-4">
              <nav className="-mb-px flex space-x-8">
                <button
                  className={`${
                    activeTab === 'resume'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('resume')}
                  aria-label="View resume"
                  aria-selected={activeTab === 'resume'}
                >
                  <FiFileText className="inline-block mr-2 h-4 w-4" />
                  Resume
                </button>
                <button
                  className={`${
                    activeTab === 'coverLetter'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('coverLetter')}
                  disabled={!application.coverLetterId}
                  aria-label="View cover letter"
                  aria-selected={activeTab === 'coverLetter'}
                >
                  <FiMail className="inline-block mr-2 h-4 w-4" />
                  Cover Letter
                  {!application.coverLetterId && (
                    <span className="ml-2 text-xs text-gray-400">(Not Available)</span>
                  )}
                </button>

              </nav>
            </div>
            
            {/* Content area */}
            <div className="mt-4">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
                </div>
              ) : activeTab === 'resume' ? (
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {resumeData ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Professional Summary</h4>
                        <p className="mt-1 text-sm text-gray-600">{toDisplayText(resumeData.content.summary)}</p>
                      </div>
                      
                      {resumeData.content.skills && resumeData.content.skills.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Skills</h4>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {resumeData.content.skills.flatMap((skill, index) => {
                              if (typeof skill === 'string') {
                                return [<span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">{skill}</span>];
                              }
                              if (skill && typeof skill === 'object' && Array.isArray(skill.details)) {
                                return skill.details.filter(d => typeof d === 'string').map((d, i) => (
                                  <span key={`${index}-${i}`} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">{d}</span>
                                ));
                              }
                              return [<span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">{toDisplayText(skill)}</span>];
                            })}
                          </div>
                        </div>
                      )}
                      
                      {resumeData.content.experience && resumeData.content.experience.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Experience</h4>
                          <div className="mt-1 space-y-3">
                            {resumeData.content.experience.map((exp, index) => (
                              <div key={index} className="border-l-2 border-primary-200 pl-3">
                                <div className="font-medium text-sm">{exp.position} at {exp.company}</div>
                                <div className="text-xs text-gray-500">{exp.start} - {exp.end || 'Present'}</div>
                                <ul className="mt-1 list-disc list-inside text-sm text-gray-600">
                                  {renderExperienceDetails(exp)}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {resumeData.content.education && resumeData.content.education.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Education</h4>
                          <div className="mt-1 space-y-2">
                            {resumeData.content.education.map((edu, index) => (
                              <div key={index}>
                                <div className="font-medium text-sm">{edu.school}</div>
                                <div className="text-xs text-gray-600">{edu.major}{edu.minor ? `, Minor: ${edu.minor}` : ''}</div>
                                <div className="text-xs text-gray-500">{edu.start} - {edu.end || 'Present'}</div>
                                {edu.courses && edu.courses.length > 0 && (
                                  <div className="text-xs text-gray-500 italic mt-1">
                                    <em>Relevant Courses: {edu.courses.map(c => typeof c === 'string' ? c : toDisplayText(c)).join(', ')}</em>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">Resume data not available</p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {coverLetterData ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">{toDisplayText(coverLetterData.content?.greeting)}</p>
                      <p className="text-sm text-gray-600">{toDisplayText(coverLetterData.content?.introduction)}</p>
                      
                      {Array.isArray(coverLetterData.content?.body) && coverLetterData.content.body.map((paragraph, idx) => (
                        <p key={idx} className="text-sm text-gray-600">{toDisplayText(paragraph)}</p>
                      ))}
                      
                      <p className="text-sm text-gray-600">{toDisplayText(coverLetterData.content?.conclusion)}</p>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{toDisplayText(coverLetterData.content?.signature)}</p>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">
                      {application.coverLetterId ? 
                        "Failed to load cover letter data" : 
                        "No cover letter was attached to this application"}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              aria-label="Close modal"
            >
              Close
            </button>
            {activeTab === 'resume' && (
              <button
                type="button"
                disabled={generatingPDF}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => generatePDF('resume')}
                aria-label={generatingPDF ? 'Preparing resume PDF' : 'Download resume as PDF'}
              >
                {generatingPDF
                  ? <FiLoader className="-ml-1 mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  : <FiDownload className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />}
                {generatingPDF ? 'Preparing…' : 'Download Resume PDF'}
              </button>
            )}
            {activeTab === 'coverLetter' && application.coverLetterId && (
              <button
                type="button"
                disabled={generatingPDF}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => generatePDF('coverLetter')}
                aria-label={generatingPDF ? 'Preparing cover letter PDF' : 'Download cover letter as PDF'}
              >
                {generatingPDF
                  ? <FiLoader className="-ml-1 mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  : <FiDownload className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />}
                {generatingPDF ? 'Preparing…' : 'Download Cover Letter PDF'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Resume Style Modal */}
      <ResumeStyleModal
        show={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        resumeId={application?.resumeId}
        versionId={application?.versionId}
      />
    </div>
  );
});

export default ApplicationDetailModal;